import { useState, useEffect } from 'react';
import { useAppState } from '../hooks/useAppState';
import { getTheme } from '../lib/theme';
import * as api from '../lib/api';
import { Send, Copy, Check, Mail, Link2, Users, Clock, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';

export default function EnviarLinksPage() {
  const { state } = useAppState();
  const dark = state.darkMode;
  const t = getTheme(dark);
  const centro = state.centrosTrabajo[state.centroActualIdx];
  const trabajadores = centro?.trabajadores || [];

  const [selected, setSelected] = useState(new Set());
  const [tokens, setTokens] = useState([]);  // Generated tokens
  const [existingTokens, setExistingTokens] = useState([]); // Previously generated
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(null);
  const [loadingTokens, setLoadingTokens] = useState(true);

  const baseUrl = window.location.origin;

  // Load existing tokens
  useEffect(() => {
    if (!state.empresaId) return;
    api.getTokens(state.empresaId)
      .then(t => { setExistingTokens(t); setLoadingTokens(false); })
      .catch(() => setLoadingTokens(false));
  }, [state.empresaId]);

  const toggleAll = () => {
    if (selected.size === trabajadores.length) setSelected(new Set());
    else setSelected(new Set(trabajadores.map(t => t.id)));
  };

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const handleGenerate = async () => {
    if (selected.size === 0) return;
    setGenerating(true);
    try {
      const res = await api.generarTokens(state.empresaId, centro.id, Array.from(selected));
      setTokens(res.tokens);
      setSelected(new Set());
      // Refresh existing tokens
      const updated = await api.getTokens(state.empresaId);
      setExistingTokens(updated);
    } catch (err) {
      alert('Error generando links: ' + err.message);
    }
    setGenerating(false);
  };

  const copyLink = (token) => {
    const url = `${baseUrl}/portal/${token}`;
    navigator.clipboard.writeText(url);
    setCopied(token);
    setTimeout(() => setCopied(null), 2000);
  };

  const mailtoLink = (token, nombre, contacto) => {
    const url = `${baseUrl}/portal/${token}`;
    const subject = encodeURIComponent('Evaluación NOM-035 — Tu participación es importante');
    const body = encodeURIComponent(
      `Hola ${nombre.split(' ')[0]},\n\n` +
      `Como parte de nuestro compromiso con tu bienestar laboral, te invitamos a completar la evaluación de riesgos psicosociales conforme a la NOM-035.\n\n` +
      `Tu participación es voluntaria y confidencial.\n\n` +
      `Accede aquí: ${url}\n\n` +
      `El link expira en 30 días.\n\n` +
      `Gracias por tu colaboración.`
    );
    return `mailto:${contacto}?subject=${subject}&body=${body}`;
  };

  // Get status for a worker from existing tokens
  const getWorkerStatus = (tid) => {
    const workerTokens = existingTokens.filter(t => t.trabajador_id === tid && t.centro_id === centro?.id);
    if (workerTokens.length === 0) return null;
    // Return the most recent
    const sorted = workerTokens.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    return sorted[0];
  };

  const STATUS_COLORS = {
    pendiente: { bg: '#fef3c7', color: '#92400e', icon: Clock },
    en_progreso: { bg: '#dbeafe', color: '#1e40af', icon: RefreshCw },
    completado: { bg: '#dcfce7', color: '#166534', icon: CheckCircle },
    rechazado: { bg: '#fee2e2', color: '#991b1b', icon: AlertCircle },
  };

  const StatusBadge = ({ status }) => {
    const s = STATUS_COLORS[status] || STATUS_COLORS.pendiente;
    const Icon = s.icon;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 6, background: s.bg, color: s.color, fontSize: '0.7rem', fontWeight: 600 }}>
        <Icon size={12} /> {status}
      </span>
    );
  };

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: '1.3rem', fontWeight: 700, color: t.textStrong, margin: 0 }}>Portal del Trabajador</h1>
          <p style={{ fontSize: '0.82rem', color: t.textMuted, margin: '4px 0 0' }}>Genera links individuales para que los trabajadores respondan desde su celular</p>
        </div>
      </div>

      {/* Generate section */}
      <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 14, padding: 20, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Link2 size={18} style={{ color: t.accent }} />
            <span style={{ fontWeight: 600, fontSize: '0.95rem', color: t.textStrong }}>Generar links</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={toggleAll} style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${t.border}`, background: 'transparent', color: t.textMuted, fontSize: '0.78rem', cursor: 'pointer' }}>
              {selected.size === trabajadores.length ? 'Deseleccionar' : 'Seleccionar'} todos
            </button>
            <button onClick={handleGenerate} disabled={selected.size === 0 || generating} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '6px 16px', borderRadius: 8,
              border: 'none', background: selected.size > 0 ? t.accent : t.border,
              color: selected.size > 0 ? '#fff' : t.textFaint, fontSize: '0.82rem', fontWeight: 600,
              cursor: selected.size > 0 ? 'pointer' : 'default', opacity: generating ? 0.7 : 1,
            }}>
              <Send size={14} /> {generating ? 'Generando...' : `Generar (${selected.size})`}
            </button>
          </div>
        </div>

        {/* Worker list */}
        <div style={{ maxHeight: 300, overflowY: 'auto' }}>
          {trabajadores.map(trab => {
            const tokenInfo = getWorkerStatus(trab.id);
            const isSelected = selected.has(trab.id);
            return (
              <div key={trab.id} onClick={() => toggle(trab.id)} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                borderRadius: 8, cursor: 'pointer', marginBottom: 2,
                background: isSelected ? (dark ? 'rgba(99,102,241,0.1)' : '#eef2ff') : 'transparent',
              }}>
                <div style={{
                  width: 20, height: 20, borderRadius: 6, border: `2px solid ${isSelected ? t.accent : t.border}`,
                  background: isSelected ? t.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {isSelected && <Check size={12} style={{ color: '#fff' }} />}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 500, color: t.textStrong }}>{trab.nombreCompleto}</div>
                  <div style={{ fontSize: '0.72rem', color: t.textFaint }}>
                    {trab.contacto || 'Sin contacto'} · {trab.metodoInvitacion || 'No asignado'}
                  </div>
                </div>
                {tokenInfo && <StatusBadge status={tokenInfo.status} />}
              </div>
            );
          })}
        </div>
      </div>

      {/* Recently generated tokens */}
      {tokens.length > 0 && (
        <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 14, padding: 20, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <CheckCircle size={18} style={{ color: '#10b981' }} />
            <span style={{ fontWeight: 600, fontSize: '0.95rem', color: t.textStrong }}>{tokens.length} links generados</span>
          </div>
          {tokens.map(tk => (
            <div key={tk.token} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
              borderRadius: 10, border: `1px solid ${t.border}`, marginBottom: 8,
              background: dark ? 'rgba(255,255,255,0.02)' : '#f9fafb',
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, color: t.textStrong }}>{tk.trabajador_nombre}</div>
                <div style={{ fontSize: '0.7rem', color: t.textFaint, fontFamily: 'monospace', marginTop: 2 }}>
                  {baseUrl}/portal/{tk.token.slice(0, 8)}...
                </div>
              </div>
              <button onClick={() => copyLink(tk.token)} style={{
                display: 'flex', alignItems: 'center', gap: 4, padding: '7px 12px',
                borderRadius: 8, border: `1px solid ${t.border}`, background: copied === tk.token ? '#10b981' : 'transparent',
                color: copied === tk.token ? '#fff' : t.textMuted, fontSize: '0.75rem', cursor: 'pointer',
              }}>
                {copied === tk.token ? <><Check size={12} /> Copiado</> : <><Copy size={12} /> Copiar</>}
              </button>
              {tk.contacto && tk.contacto.includes('@') && (
                <a href={mailtoLink(tk.token, tk.trabajador_nombre, tk.contacto)} style={{
                  display: 'flex', alignItems: 'center', gap: 4, padding: '7px 12px',
                  borderRadius: 8, border: `1px solid ${t.border}`, background: 'transparent',
                  color: t.textMuted, fontSize: '0.75rem', textDecoration: 'none',
                }}>
                  <Mail size={12} /> Email
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Status overview */}
      {!loadingTokens && existingTokens.filter(t => t.centro_id === centro?.id).length > 0 && (
        <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 14, padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <Users size={18} style={{ color: t.textMuted }} />
            <span style={{ fontWeight: 600, fontSize: '0.95rem', color: t.textStrong }}>Estado de respuestas</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: 16 }}>
            {['pendiente', 'en_progreso', 'completado', 'rechazado'].map(status => {
              const count = existingTokens.filter(t => t.centro_id === centro?.id && t.status === status).length;
              if (count === 0) return null;
              const s = STATUS_COLORS[status];
              return (
                <div key={status} style={{ padding: '12px 14px', borderRadius: 10, background: s.bg, textAlign: 'center' }}>
                  <div style={{ fontSize: '1.4rem', fontWeight: 700, color: s.color }}>{count}</div>
                  <div style={{ fontSize: '0.7rem', fontWeight: 600, color: s.color, textTransform: 'capitalize' }}>{status.replace('_', ' ')}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
